<template>
    <div class="dashboard">
        <TopNav />

        <div class="main-container">
            <!-- 左侧菜单组件 -->
            <aside class="sidebar">
                <div class="sidebar-header">
                    <div class="user-info">
                        <div class="user-avatar">
                            <img v-if="userInfo && userInfo.avatar" :src="userInfo.avatar" alt="avatar">
                            <i v-else class="el-icon-user-solid" />
                        </div>
                        <div class="user-detail" v-if="userInfo && userInfo.nickname">
                            <h4>{{ userInfo.nickname }}</h4>
                            <p v-if="userInfo.email">{{ userInfo.email }}</p>
                        </div>
                        <div class="user-detail" v-else>
                            <h4>{{ userInfo ? $t('Other.去登录') : $t('MyLayout.创作者') }}</h4>
                        </div>
                    </div>
                </div>

                <div class="sidebar-menu">
                    <router-link v-for="item in list" :key="item.path" :to="item.path" class="sidebar-item"
                        :class="{ 'active': isActive(item.path) }">
                        <i :class="item.icon" />
                        <span style="flex: 1;">{{ $t(item.name) }}</span>
                        <span v-if="item.badge" class="badge">{{ item.badge }}</span>
                    </router-link>
                </div>

                <div class="sidebar-footer">
                    <div class="logout-btn" @click="handleLogout">
                        <i class="el-icon-switch-button" />
                        <span>{{ $t('MyLayout.退出登录') }}</span>
                    </div>
                </div>
            </aside>

            <!-- 右侧内容区 -->
            <main class="content-area">
                <router-view />
            </main>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import TopNav from '@/components/TopNav.vue'

export default {
    name: 'MyLayout',
    components: {
        TopNav
    },
    data() {
        return {
            list: [
                {
                    path: '/my',
                    name: 'MyLayout.概览',
                    icon: 'el-icon-data-line'
                },
                {
                    path: '/my/my-video',
                    name: 'MyLayout.我的创作',
                    icon: 'el-icon-folder-opened'
                },
                {
                    path: '/my/membership',
                    name: 'MyLayout.会员中心',
                    icon: 'el-icon-trophy-1'
                },
                {
                    path: '/my/credit',
                    name: 'MyLayout.额度管理',
                    icon: 'el-icon-coin'
                },
                {
                    path: '/my/billing',
                    name: 'MyLayout.账单管理',
                    icon: 'el-icon-document-copy'
                },
                {
                    path: '/my/task',
                    name: 'MyLayout.生成任务',
                    icon: 'el-icon-more-outline'
                }
            ],
            activeTab: ''
        }
    },
    watch: {
        '$route.path': {
            handler(newPath) {
                this.activeTab = newPath
            },
            immediate: true
        }
    },
    computed: {
        ...mapState(['userInfo']),
    },
    mounted() {
        if (!this.userInfo) {
            this.$confirm(this.$t('Other.请登录后查看'), this.$t('MyLayout.提示'), {
                confirmButtonText: this.$t('MyLayout.确定'),
                cancelButtonText: this.$t('MyLayout.取消'),
                type: 'warning'
            }).then(() => {
                if (this.$route.path.startsWith('/my')) {
                    this.$router.push('/login')
                }
            }).catch(() => {
                if (this.$route.path.startsWith('/my')) {
                    this.$router.push('/')
                }
            })
        } else {
            this.activeTab = this.$route.path
        }
    },
    methods: {
        isActive(path) {
            const currentPath = this.$route.path
            if (path === '/my') {
                return currentPath === '/my' || currentPath === '/my/'
            }
            return currentPath === path
        },
        handleLogout() {
            this.$confirm(this.$t('MyLayout.确定要退出登录吗？'), this.$t('MyLayout.提示'), {
                confirmButtonText: this.$t('MyLayout.确定'),
                cancelButtonText: this.$t('MyLayout.取消'),
                type: 'warning'
            }).then(() => {
                this.$store.dispatch("logout").then(() => {
                    this.userMenuOpen = false
                    this.$message.success(this.$t('MyLayout.退出成功'))
                    if (this.$route.path.startsWith('/my')) {
                        this.$router.push('/')
                    }
                }).catch(() => { })
            }).catch(() => { })
        }
    }
}
</script>

<style lang="scss" scoped>
.dashboard {
    background: #08080f;
    color: #f1f5f9;
    min-height: 100vh;
    padding-top: 72px;

    .main-container {
        max-width: 1400px;
        margin: 0 auto;
        padding: 2rem;
        display: grid;
        grid-template-columns: 280px 1fr;
        gap: 2rem;

        .sidebar {
            position: sticky;
            top: 100px;
            height: fit-content;
            background: rgba(15, 15, 26, 0.6);
            backdrop-filter: blur(10px);
            border: 1px solid rgba(255, 255, 255, 0.08);
            border-radius: 20px;
            overflow: hidden;

            .sidebar-header {
                padding: 1.5rem;
                border-bottom: 1px solid rgba(255, 255, 255, 0.08);
                background: linear-gradient(135deg, rgba(99, 102, 241, 0.1), rgba(168, 85, 247, 0.05));

                .user-info {
                    display: flex;
                    align-items: center;
                    gap: 1rem;

                    .user-avatar {
                        width: 56px;
                        height: 56px;
                        background: linear-gradient(135deg, #6366f1, #a855f7);
                        border-radius: 50%;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        color: white;
                        overflow: hidden;

                        img {
                            width: 100%;
                            height: 100%;
                            object-fit: cover;
                        }

                        i {
                            font-size: 1rem;
                            color: white;
                        }
                    }

                    .user-detail {
                        flex: 1;

                        h4 {
                            font-size: 1rem;
                            font-weight: 600;
                            margin-bottom: 0.25rem;
                            color: #f1f5f9;
                        }

                        p {
                            font-size: 0.75rem;
                            color: #94a3b8;
                        }
                    }
                }
            }

            .sidebar-menu {
                padding: 1rem;

                .sidebar-item {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    padding: 0.875rem 1rem;
                    border-radius: 12px;
                    color: #94a3b8;
                    text-decoration: none;
                    margin-bottom: 0.5rem;
                    position: relative;

                    i {
                        width: 20px;
                        font-size: 1.125rem;
                    }

                    span {
                        font-size: 0.9375rem;
                    }

                    .badge {
                        display: inline-block;
                        padding: 0.125rem 0.5rem;
                        background: rgba(99, 102, 241, 0.2);
                        border-radius: 20px;
                        font-size: 0.7rem;
                        color: #818cf8;
                        font-weight: 500;
                        width: auto;
                    }

                    &:hover {
                        background: rgba(99, 102, 241, 0.1);
                        color: #f1f5f9;
                        transform: translateX(4px);
                    }

                    &.active {
                        background: linear-gradient(135deg, rgba(99, 102, 241, 0.2), rgba(168, 85, 247, 0.2));
                        color: #f1f5f9;
                        border: 1px solid rgba(99, 102, 241, 0.3);

                        &::before {
                            content: '';
                            position: absolute;
                            left: -1rem;
                            top: 50%;
                            transform: translateY(-50%);
                            width: 3px;
                            height: 20px;
                            background: linear-gradient(135deg, #6366f1, #a855f7);
                            border-radius: 0 3px 3px 0;
                        }
                    }
                }
            }

            .sidebar-footer {
                padding: 1rem;
                border-top: 1px solid rgba(255, 255, 255, 0.08);
                margin-top: 0.5rem;

                .logout-btn {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    padding: 0.875rem 1rem;
                    border-radius: 12px;
                    color: #94a3b8;
                    cursor: pointer;
                    transition: all 0.3s;

                    i {
                        font-size: 1.125rem;
                    }

                    span {
                        font-size: 0.9375rem;
                    }

                    &:hover {
                        background: rgba(239, 68, 68, 0.1);
                        color: #ef4444;
                        transform: translateX(4px);
                    }
                }
            }
        }

        .content-area {
            min-height: 80vh;
        }
    }
}

// 响应式
@media (max-width: 1024px) {
    .dashboard {
        .main-container {
            grid-template-columns: 1fr !important;
            padding: 1.5rem;
        }

        .sidebar {
            display: none !important;
        }
    }
}

@media (max-width: 768px) {
    .dashboard {
        padding-top: 60px;

        .main-container {
            padding: 1rem;
        }
    }
}
</style>